import React, { useState, useEffect, useMemo } from "react";
import { Stack, Box, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { FullPageContainer } from "../../components/layout/container/FullPageContainer";
import colorsCustom from "../../designTokens/colorsCustom";
import { useIsMobile } from "../../hooks/useIsMobile";
import TypingEffect from "../../components/patterns/typoraphy/TypingEffect";
import FadeInTypography from "../../components/patterns/typoraphy/FadeInTypography";
import { PageContainer } from "../../components/Container/PageContainer";
import ParallaxContainer from "../../components/scroll/ParallaxContainer";
import ParallaxLayer from "../../components/scroll/ParallaxLayer";
import OptimizedAvatar from "../../components/Avatar/OptimizedAvatar";

// Firebase Storage URL for video streaming
const WRITING_MAN_VIDEO_URL = "https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fwritting_man.mp4?alt=media";
// Placeholder image while video loads (optional - can be added later)
const VIDEO_POSTER_URL = null; // 포스터 이미지는 나중에 추가

export const AllCollectionHeroSection = ({
  titleKey,
  descriptionKey,
  bg,
  c,
  style = {},
  collections = [],
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile("xl");
  const isTablet = useIsMobile("lg");
  const isSmallMobile = useIsMobile("sm");
  const theme = useTheme();
  const [animatedAvatars, setAnimatedAvatars] = useState([]);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // 컬렉션에서 고유한 사용자 추출 (중복 제거)
  const uniqueUsers = useMemo(() => {
    const userMap = new Map();

    collections.forEach((collection) => {
      if (collection.userID && collection.username) {
        if (!userMap.has(collection.userID)) {
          userMap.set(collection.userID, {
            id: collection.userID,
            username: collection.username,
            name: collection.name,
            profileImage: collection.profileImage,
          });
        }
      }
    });

    return Array.from(userMap.values()).slice(0, 10); // 최대 10명만 표시
  }, [collections]);

  // 아바타 애니메이션 상태 관리
  useEffect(() => {
    console.log("Collections:", collections.length);
    console.log("UniqueUsers:", uniqueUsers.length);
    console.log("isMobile:", isMobile);

    // 테스트용 아바타 생성 (데이터가 없어도 표시)
    const testAvatars = Array.from({ length: 10 }, (_, index) => ({
      id: `test-${index}`,
      username: `User${index}`,
      name: `Test User ${index}`,
      profileImage: null,
      x: Math.random() * 80 + 10,
      y: Math.random() * 80 + 10,
      depthZ: isMobile
        ? Math.floor(Math.random() * 30) + 10  // mobile: 10-40
        : Math.floor(Math.random() * 600) + 200, // desktop: 200-800
      scale: isMobile
        ? 1.2 - ((Math.floor(Math.random() * 5) + 2) / 6) * 0.6
        : 1.2 - ((Math.floor(Math.random() * 5) + 2) / 6) * 0.6,
      isVisible: true,
      // 30% 확률로 텍스트 위로 올라가는 아바타
      isAboveText: Math.random() < 0.3,
      // 랜덤 좌우 오프셋 (-200px ~ +200px)
      randomOffsetX: isMobile
        ? (Math.random() - 0.5) * 100
        : (Math.random() - 0.5) * 400,
      // 랜덤 페이드인 딜레이 (0~2초)
      fadeDelay: Math.random() * 2,
      // 둥둥 떠다니는 효과를 위한 애니메이션 속성
      floatDelay: Math.random() * 3, // 0-3초 랜덤 딜레이
      floatDuration: 3 + Math.random() * 2, // 3-5초 랜덤 지속시간
    }));

    // 실제 사용자 데이터가 있으면 사용, 없으면 테스트 데이터 사용
    if (uniqueUsers.length > 0) {
      // 랜덤으로 섞어서 10개만 선택
      const shuffledUsers = [...uniqueUsers]
        .sort(() => Math.random() - 0.5)
        .slice(0, 10);
      const avatarsWithAnimation = shuffledUsers.map((user, index) => {
        const x = Math.random() * 80 + 10;
        const y = Math.random() * 80 + 10;
        const depthZ = isMobile
          ? Math.floor(Math.random() * 30) + 10  // mobile: 10-40
          : Math.floor(Math.random() * 600) + 200; // desktop: 200-800
        const scale = isMobile
          ? 1.2 - ((depthZ - 10) / 30) * 0.6  // depthZ 10-40 범위를 0-1로 정규화
          : 1.2 - ((depthZ - 200) / 600) * 0.6; // depthZ 200-800 범위를 0-1로 정규화

        return {
          ...user,
          x,
          y,
          depthZ,
          scale,
          isVisible: true,
          // 30% 확률로 텍스트 위로 올라가는 아바타
          isAboveText: Math.random() < 0.3,
          // 랜덤 좌우 오프셋 (-200px ~ +200px)
          randomOffsetX: isMobile
            ? (Math.random() - 0.5) * 100
            : (Math.random() - 0.5) * 400,
          // 랜덤 페이드인 딜레이 (0~2초)
          fadeDelay: Math.random() * 2,
          // 둥둥 떠다니는 효과를 위한 애니메이션 속성
          floatDelay: Math.random() * 3, // 0-3초 랜덤 딜레이
          floatDuration: 3 + Math.random() * 2, // 3-5초 랜덤 지속시간
        };
      });
      console.log("Using real avatars:", avatarsWithAnimation.length);
      console.log("Sample avatar depthZ values:", avatarsWithAnimation.slice(0, 3).map(a => a.depthZ));
      setAnimatedAvatars(avatarsWithAnimation);
    } else {
      console.log("Using test avatars:", testAvatars.length);
      setAnimatedAvatars(testAvatars);
    }
  }, [uniqueUsers, collections, isMobile]);

  // 아바타 컴포넌트 - 최적화된 아바타 사용
  const UserAvatar = ({ user }) => {
    const avatarSize = isMobile ? 35 * user.scale : 80 * user.scale;
    const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);

    // 각 아바타마다 고유한 float 애니메이션 랜덤 값
    const floatOffset = Math.random() * 20 - 10; // -10px ~ +10px

    return (
      <>
        {/* 고유한 키프레임 애니메이션 스타일 추가 */}
        <style>
          {`
            @keyframes float-${user.id} {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(${floatOffset}px); }
            }
          `}
        </style>
        <div
          style={{
            opacity: isAvatarLoaded ? 1 : 0,
            transform: isAvatarLoaded ? "translateY(0)" : "translateY(20px)",
            transition: `all ${0.8 + user.fadeDelay}s cubic-bezier(0.4, 0, 0.2, 1)`,
            transitionDelay: `${user.fadeDelay}s`,
            animation: isAvatarLoaded ? `float-${user.id} ${user.floatDuration}s ease-in-out infinite` : "none",
            animationDelay: `${user.floatDelay}s`,
          }}
        >
          <OptimizedAvatar
            userID={user.id}
            username={user.username}
            size={avatarSize}
            onLoadChange={setIsAvatarLoaded}
            style={{
              filter: "grayscale(100%)",
            }}
          />
        </div>
      </>
    );
  };

  return (
    <>
      {/* 히어로 섹션에만 패럴럭스 적용 */}
      <FullPageContainer bg={bg} c={c} style={style}>
        <ParallaxContainer
          maxZ={isMobile ? 50 : 1000}
          style={{ minHeight: "100vh" }}
        >
          <PageContainer expand={true} style={{ position: "relative" }}>
            {/* 타이틀과 설명 - 가장 느린 패럴럭스 */}
            <ParallaxLayer
              depthZ={isMobile ? 45 : 950}
              style={{
                padding: 0,
                position: "relative",
                zIndex: 10,
                alignItems: "flex-start",
                minHeight: "100vh",
                paddingTop: "10vh",
              }}
            >
              <Stack spacing={3} sx={{ position: "relative", zIndex: 10 }}>
                <FadeInTypography
                  text={t(titleKey)}
                  variant={isMobile ? "h2" : "h1"}
                  direction="up"
                  speed={600}
                  wordDelay={240}
                  sx={{
                    fontSize: isSmallMobile
                      ? "2.5rem"
                      : isTablet
                      ? "3.5rem"
                      : isMobile
                      ? "4rem"
                      : "8rem",
                    fontFamily: '"source-han-serif-korean", "Noto Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", serif',
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    position: "relative",
                    zIndex: 10,
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isMobile ? "flex-start" : "center",
                    position: "relative",
                    width: isMobile ? "100%" : "fit-content",
                    maxWidth: "100%",
                    zIndex: 10,
                  }}
                >
                  <TypingEffect
                    texts={[t(descriptionKey)]}
                    typingSpeed={80}
                    startDelay={800}
                    cursorType="circle"
                    fontFamily='"source-han-serif-korean", "Noto Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", serif'
                    fontWeight={600}
                    textAlign={isMobile ? "left" : "center"}
                    cursorColor={theme.palette.text.primary}
                    textColor={colorsCustom.ui.dark.bold}
                    variant={isMobile ? "h5" : "h3"}
                  />
                </Box>
              </Stack>
            </ParallaxLayer>

            {/* 비디오 - 중간 속도 패럴럭스 */}
            <ParallaxLayer
              depthZ={isMobile ? 40 : 600}
              style={{
                padding: 0,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: isMobile ? "240px" : "0",
                  right: 0,
                  width: isMobile ? "100%" : "70vw",
                  height: isMobile ? "auto" : "auto",
                  overflow: "hidden",
                  maxWidth: "100%",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(to right, ${theme.palette.background.default} 0%, ${theme.palette.background.default}99 10%, ${theme.palette.background.default}80 25%, transparent 50%),
                                linear-gradient(to bottom, ${theme.palette.background.default} 0%, ${theme.palette.background.default}99 10%, ${theme.palette.background.default}80 25%, transparent 50%),
                                linear-gradient(to top, ${theme.palette.background.default} 0%, ${theme.palette.background.default}99 10%, ${theme.palette.background.default}80 25%, transparent 50%)`,
                    pointerEvents: "none",
                    zIndex: 1,
                  },
                }}
              >
                {!videoError && (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={VIDEO_POSTER_URL || undefined}
                    onLoadedData={() => setVideoLoaded(true)}
                    onError={() => {
                      console.error("Video failed to load");
                      setVideoError(true);
                    }}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "100%",
                      objectFit: "cover",
                      filter:
                        "sepia(0.2) saturate(0) hue-rotate(220deg) brightness(0.9) contrast(1.2)",
                      opacity: videoLoaded ? 1 : 0,
                      transition: "opacity 0.5s ease-in-out",
                    }}
                    // 캐싱 개선을 위한 속성
                    crossOrigin="anonymous"
                  >
                    <source 
                      src={`${WRITING_MAN_VIDEO_URL}&cache-control=public,max-age=31536000`} 
                      type="video/mp4" 
                    />
                    {/* Fallback for browsers that don't support video */}
                    Your browser does not support the video tag.
                  </video>
                )}
                {/* Fallback image if video fails to load */}
                {videoError && (
                  <img
                    src={VIDEO_POSTER_URL}
                    alt="Writing man"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "100%",
                      objectFit: "cover",
                      filter:
                        "sepia(0.2) saturate(0) hue-rotate(220deg) brightness(0.9) contrast(1.2)",
                    }}
                  />
                )}
              </Box>
            </ParallaxLayer>
          </PageContainer>
        </ParallaxContainer>
      </FullPageContainer>

      {/* 기존 아바타 패럴럭스 영역 - 개별 패럴럭스 유지 */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isMobile ? "80vh" : "100vh", // 모바일에서는 높이 감소
          marginTop: isMobile ? "-80vh" : "-80vh", // 모바일에서는 간격 조정
          pointerEvents: "none",
        }}
      >
        <ParallaxContainer maxZ={isMobile ? 50 : 1000}>
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            sx={{
              width: "100%",
              margin: "0 auto",
              padding: isMobile ? "1rem" : "2rem",
              minHeight: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {animatedAvatars.map((user, index) => (
              <Grid
                key={user.id}
                size={isMobile ? 4 : 3}
                sx={{
                  ...(index % 2 === 1 && { paddingTop: "1.5rem !important" }), // 홀수 인덱스에 상단 패딩 감소
                }}
              >
                <ParallaxLayer
                  depthZ={user.depthZ}
                  style={{
                    padding: 0, // 패럴럭스 기본 패딩 제거
                    position: "relative",
                    zIndex: user.isAboveText ? 15 : 5,
                    height: isMobile ? "60px" : "80px", // 아바타 컨테이너 높이 감소
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: `translate(-50%, -50%) translateX(${user.randomOffsetX}px)`,
                      zIndex: user.isAboveText ? 15 : 5,
                    }}
                  >
                    <UserAvatar user={user} />
                  </div>
                </ParallaxLayer>
              </Grid>
            ))}
          </Grid>
        </ParallaxContainer>
      </Box>
    </>
  );
};
